import { request } from "@/axios";
import type { ListCommonParams, ListResponse } from "@/types/list";
import type { RightsState, PowerConsumeDetailsState, PowerGainDetailsState, OrderState } from "@/types/power";
import type { ServerResponse } from "@/types/request";
import type { PayChannel } from "@/types/pay";
import type { CorporatePreOrderCredentials } from "@xmiles/holopix-api";
import type { PreOrderPaymentCredentials } from "@/assets/pay/preOrderPayment";

/** 查询权益；支付基线可关闭底层提示，由当前支付会话统一处理错误。 */
export function queryRights(notToast = false) {
  return request<RightsState>({
    api: "/api/rights/getRights",
    method: "post",
    data: {},
    notToast
  });
}

// 算力明细
interface QueryPowerConsumeData extends ListCommonParams {
  type: "sub";
}
interface QueryPowerGainData extends ListCommonParams {
  type: "add";
}
export function queryPowerDetails(data: QueryPowerConsumeData): Promise<ServerResponse<ListResponse<PowerConsumeDetailsState[]>>>;
export function queryPowerDetails(data: QueryPowerGainData): Promise<ServerResponse<ListResponse<PowerGainDetailsState[]>>>;
export function queryPowerDetails(data: QueryPowerConsumeData | QueryPowerGainData) {
  if (data.type === "add") {
    return request<ListResponse<PowerGainDetailsState[]>>({
      api: "/api/rights/pointsRecord",
      method: "post",
      data,
      notToast: false
    });
  }

  return request<ListResponse<PowerConsumeDetailsState[]>>({
    api: "/api/rights/pointsRecord",
    method: "post",
    data,
    notToast: false
  });
}

// 支付下单
export interface AddOrderData {
  /** PC 扫码支付由预下单接口直接返回支付凭证。 */
  payModel?: "pc";
  /** 签约类型：1 签约并支付，2 签约后支付。 */
  signType?: number;
  purchasesCount?: number;
  /** 订单类型,team_add_member 为增加席位数 */
  orderType?: "member" | "points" | "team_member" | "team_add_member" | "team_points"
    | "api_resource_package" | "api_standard_package" | "api_qps_package";
  payChannel?: PayChannel;
  memberType?: "pro" | "plus" | "ultra"; // 会员等级（ultra：旗舰版 VVIP）
  duration?:
    | "month"
    | "season"
    | "half_year"
    | "year"
    | "year_v1"
    | "one_year"
    | "two_year"
    | "three_year"
    | "week"
    | "halfSubscribe"
    | "api_day"
    | "api_month"
    | "subscribe"; // 开通会员时间（含前端下单口径，后端按需识别）
  pointsType?: string; // 算力包
  headCount?: number; // 人员数量
  teamName?: string; // 团队名称
  /** 增加席位数 */
  addHeadCount?: number;
  /** 团队id */
  teamId?: number;
  /** 商品id */
  commodityId?: string;
  /** 买断。试用声明只对月付订阅生效，买断优先于试用。 */
  isBuyout?: boolean;
  /** 是否按 7 天低价试用下单。必须显式 true 才返回试用商品。 */
  isTrial?: boolean;
}

interface AddOrderResponse {
  qrCode: string;
  innerOrderId: string;
}

export function addOrder(data: AddOrderData) {
  if (data.orderType === "member" || data.orderType === "team_member") {
    delete data.pointsType;
  } else {
    delete data.duration;
    delete data.memberType;
  }

  return request<AddOrderResponse>({
    api: "/api/order/addOrder",
    method: "post",
    data,
    notToast: false
  });
}

// 查询订单
export function queryOrderList(data: ListCommonParams & { selType?: 1 | 2 }) {
  return request<ListResponse<OrderState[]>>({
    api: "/api/order/orderList",
    method: "post",
    data,
    notToast: false
  });
}

// 获取算力获取映射
export function queryGainPowerMap() {
  return request<Record<string, string>>({
    api: "/api/common/pointsRecordContentMap",
    method: "get",
    notToast: false
  });
}

// 订阅
export function addSubscribe(data: {
  memberType: string;
  signType: number; // 1: 签约并支付、2: 签约后支付
  agreementNo?: string; // 签约号
}) {
  return request<{
    qrCode: string;
    signNo: string | null;
    innerOrderId: string | null;
  }>({
    api: "/api/order/signAgreement",
    method: "post",
    data
  });
}

// 查询升级会员套餐差价
export function queryUpgradeGapAmount(data: { memberType: string; duration: string }) {
  return request<{
    upgradeGapAmount: number;
  }>({
    api: "/api/order/upgradeGapAmount",
    method: "post",
    data
  });
}

// 查询订单
import type { PayOrderStatus } from "@/types/pay";
import { ICreateContractResponse } from "@/types/teamEdition";
import { UnSignedResponse } from "@/components/CloseAutoPayButton";
export function queryOrderStatus(data: { transactionId: string }, notToast = false) {
  return request<PayOrderStatus>({
    api: "/api/order/queryOrder",
    method: "post",
    data,
    notToast
  });
}

// 查询订阅
export function querySubscribeStatus(
  data: {
    transcationId: string;
    agreementNo?: string;
  },
  notToast = false
) {
  return request<{
    status: number;
    startTime: string;
    endTime: string;
  }>({
    api: "/api/order/querySign",
    method: "post",
    data,
    notToast
  });
}

/** 升级退款明细项 */
export interface IUpRefundItem {
  transactionId?: string;
  amount?: number;
  orderId?: string;
  oldSignNo?: string | null;
}

// 获取预下单商品信息的请求参数类型
export interface IGetCommodityInfoDataReSponse extends PreOrderPaymentCredentials, CorporatePreOrderCredentials {
  prdId?: number;
  commodityId?: string;
  userId?: number;
  uniqueId?: string;
  subject?: string;
  orderType: NonNullable<AddOrderData['orderType']>;
  payChannel: "alipay" | "wechat" | "corporate";
  memberType?: string;
  duration?: string;
  pointsType?: string | null;
  totalAmount?: number;
  ctime?: string;
  teamName?: string;
  headCount?: number;
  teamId?: number | null;
  sign?: string | null;
  addHeadCount?: number | null;
  /** 当前买购买的API并发数 */
  currentQps?: number;
  /** 会员算力发放时间（续费场景为下一周期生效日） */
  memberPointsDdl?: string | null;
  /** 是否为 SaaS 订阅类型商品（支付宝 AI 订阅新方案） */
  isSaaS?: boolean;
  /** 本次预下单是否选中 7 天低价试用。以响应为准，不以请求声明为准。 */
  isTrial?: boolean;
  /** 旧订单签约号（升级场景，需要解约时返回） */
  oldSignNo?: string | null;
  /** 升级退款金额（新→新升级时可能为 0） */
  upRefundAmount?: number;
  /** 退款明细列表（新→新升级时可能为空） */
  upRefundItems?: IUpRefundItem[];
  /** 实际支付金额（升级场景优先使用该字段） */
  newTotalAmount?: number;
  /** 新会员版本每月发放算力（升级场景） */
  newPoints?: number;
  /** 新会员版本名称（升级场景） */
  newSubject?: string;
  /** 升级状态 0-正常 1-人工介入 2-后台开通会员 */
  upStatus?: number;
}

// 获取预下单商品信息
export function getCommodityInfo(data: AddOrderData, options: { signal?: AbortSignal; notToast?: boolean } = {}) {
  const api = "/api/commonOrder/getCommodityInfo";
  const requestData = { ...data };
  // 新订阅下单不再携带页面 AB 分叉。活动页旧分支仍留在展示层。
  delete (requestData as { abTest?: unknown }).abTest;

  // api开放平台原样保留数据
  const isApiOrder = requestData.orderType?.includes("api");
  if (!isApiOrder) {
    if (requestData.orderType === "member" || requestData.orderType === "team_member") {
      delete requestData.pointsType;
    } else {
      delete requestData.duration;
      delete requestData.memberType;
    }
  }

  return request<IGetCommodityInfoDataReSponse>({
    api,
    method: "post",
    data: requestData,
    // PC 支付入口统一展示错误，已关闭会话的晚到请求不应再弹底层错误。
    notToast: options.notToast ?? requestData.payModel === "pc",
    signal: options.signal
  }).catch((error) => {
    console.error(`[getCommodityInfo] ${api} 请求失败`, {
      requestData,
      code: error?.code,
      msg: error?.msg ?? error?.message,
      error
    });
    throw error;
  });
}

/**
 * 团队版升级：预校验与补差信息请求参数
 * NOTE: 该接口只用于 upStatus 校验与拦截，实际支付金额一律以 getCommodityInfo 为准
 */
export interface ITeamUpgradeRefundParams {
  /** 目标升级版本（当前仅支持升到 plus / ultra） */
  memberType: "plus" | "ultra";
  /** 固定为 team_member（升级不改席位，走会员口径） */
  orderType: "team_member";
  /** 团队 id */
  teamId: number;
}

/**
 * 团队版升级 upStatus 语义：
 * - 0：可正常升级
 * - 1：需人工介入（存在异常订单/退款等，前端拦截并引导联系客服）
 * - 2：不支持升级（oldSubject 为不支持原因文案）
 */
export type TeamUpgradeStatus = 0 | 1 | 2;

/** 团队版升级预校验与补差信息响应 */
export interface ITeamUpgradeRefundResponse {
  /** 原版本主体（upStatus=2 时承载不支持原因文案） */
  oldSubject: string;
  /** 目标版本主体 */
  newSubject: string;
  /** 原版本算力 */
  oldPoints: number;
  /** 目标版本算力 */
  newPoints: number;
  /** 目标版本原始年费总额（元） */
  newTotalAmount: number;
  /** 升级后生效开始时间 */
  startTime: string;
  /** 升级后到期时间 */
  endTime: string;
  /** 按剩余天数折算的补差金额（元），仅供参考，实付以 getCommodityInfo 为准 */
  upRefundAmount: number;
  /** 升级需补发的剩余算力，当前恒为 0 */
  upLeavePoints: number;
  /** 关联的历史交易流水，可能为空串 */
  upTransactionId: string;
  /** 升级可用性状态 */
  upStatus: TeamUpgradeStatus;
}

/**
 * 团队版升级预校验：获取 upStatus 与补差参考信息
 * 用于升级前的可用性拦截（人工介入 / 不支持）；金额展示与实付以 getCommodityInfo 为准
 */
export function teamUpgradeRefundAmount(data: ITeamUpgradeRefundParams) {
  const api = "/api/commonOrder/teamUpgradeRefundAmount";
  return request<ITeamUpgradeRefundResponse>({
    api,
    method: "post",
    data,
    notToast: false
  }).catch((error) => {
    console.error(`[teamUpgradeRefundAmount] ${api} 请求失败`, {
      requestData: data,
      code: error?.code,
      msg: error?.msg ?? error?.message,
      error
    });
    throw error;
  });
}

// 操作对公账单接口参数类型
export interface IOptCorporateOrderData {
  innerOrderId?: string; // 订单号
  transactionId?: string; // 支付流水号
  orderStatus?: "WAIT_CORPORATE_CONFIRM" | "EXPIREPAY"; // 支付状态,
  corporateName?: string; // 企业名称
  corporatePhone?: string; // 企业联系电话
  corporateUrl?: string; // 企业付款回执
}

// 操作对公账单接口
export function optCorporateOrder(data: IOptCorporateOrderData) {
  return request<{
    code: number;
    message: string;
    data: null;
  }>({
    api: "/api/order/optCorporateOrder",
    method: "post",
    data,
    notToast: false
  });
}

/**
 * 创建合同接口参数类型
 */
export interface ICreateContractParams {
  /** 购买方名称 */
  buyerName: string;
  /** 购买方电话 */
  buyerPhoneNumber: string;
  /** 通讯地址 */
  correspondenceAddress: string;
  /** 订单号,中台返回的流水号 */
  transactionId: string;
  /** 统一信用代码 */
  uniformCreditCode: string;
  /** 用户ID */
  userId: string;
  /** 唯一ID */
  uniqueId: string;
}

/**
 * 创建合同接口返回类型
 */

/**
 * 创建合同接口
 */
export function createContract(data: ICreateContractParams) {
  return request<ICreateContractResponse>({
    api: "/api/contract/createContract",
    method: "post",
    data,
    notToast: false
  });
}

/**
 * 获取中台商品信息参数类型
 */
export interface IGetGoodInfoParams {
  /** 中台流水号 */
  transcationId: string;
  /** 支付渠道: alipay、wechat、corporate */
  payChannel: "alipay" | "wechat" | "corporate";
}

/**
 * 获取中台商品信息返回类型
 */
export interface IGetGoodInfoResponse {
  /** 内部订单号 */
  innerOrderId: string;
  /** 交易ID */
  transactionId: string;
  /** 商品名称 */
  subject: string;
  /** 会员类型 */
  memberType: string;
  /** 订单类型 */
  orderType: string;
  /** 支付渠道 */
  payChannel: string;
  /** 订单状态 */
  orderStatus: string;
  /** 总金额(分) */
  totalAmount: number;
  /** 支付时间 */
  payTime: string;
  /** 开始时间 */
  startTime: string | null;
  /** 结束时间 */
  endTime: string | null;
  /** 团队名称 */
  teamName: string;
  /** 席位数 */
  headCount: number;
  /** 创建时间 */
  ctime: string;
  /** 企业ID */
  corporateId: string;
  /** 等级费率 */
  levelRate: string | null;
  /** 收款方名称 */
  ourReceiveSubjectName: string;
  /** 收款账号 */
  ourReceiveAccount: string;
  /** 收款银行 */
  ourReceiveBank: string;
}

/**
 * 获取中台商品信息
 */
export function getGoodInfo(data: IGetGoodInfoParams) {
  return request<IGetGoodInfoResponse>({
    api: "/api/commonOrder/getGoodInfo",
    method: "post",
    data,
    notToast: false
  });
}

/**
 * 获取已存在的对公支付订单
 */
export function getExistCorporateOrder() {
  return request<IGetGoodInfoResponse>({
    api: "/api/commonOrder/getExistCorporate",
    method: "post",
    data: {},
    notToast: false
  });
}

/**
 * 升级会员退款金额接口参数类型
 */
export interface IUpgradeRefundAmountParams {
  /** 时限，订单类型为member时必传：month、season、half_year、year */
  duration?: string;
  /** 会员类型 */
  memberType?: string;
  /** 订单类型：member、points、team_member、team_add_member、team_points */
  orderType: string;
  /** 目标是否买断。不传则保持旧预览口径，等接口字段上线后由卡片路径显式传入。 */
  isBuyout?: boolean;
  /** 仅购买尝鲜试用时传 true，接口按试用口径试算金额；购买月付等完整版不传（与当前是否在试用无关）。 */
  isTrial?: boolean;
}

/**
 * 升级会员退款金额响应类型
 */
export interface IUpgradeRefundAmountResponse {
  /** 会员过期时间 */
  endTime: string;
  /** 新会员版本每月发放算力 */
  newPoints: number;
  /** 新会员版本 */
  newSubject: string;
  /** 新会员版本金额（实际支付优先用此字段，不要用 upRefundAmount 推算） */
  newTotalAmount: number;
  /** 原会员版本每月发放算力 */
  oldPoints: number;
  /** 原会员版本 */
  oldSubject: string;
  /** 会员开通时间 */
  startTime: string;
  /** 剩余算力 */
  upLeavePoints: number;
  /** 退款金额（新→新 SaaS 升级时可能为 0） */
  upRefundAmount: number;
  /**
   * 抵扣金额（旧订单可退金额，SaaS 订阅升级 / 补差价场景的算力折算金额）
   * 展示「算力折算金额」时优先取此字段；缺省时可回退 upRefundAmount
   */
  upDeductAmount?: number;
  /** 状态 0-正常 1-人工介入  2-后台开通会员*/
  upStatus: number;
  /** 退款商户订单号 */
  upTransactionId: string;
  /** 是否为 SaaS 订阅类型商品升级 */
  isSaaS?: boolean;
  /** 旧订单签约号（兼容字段；优先使用 upOldSignNo） */
  oldSignNo?: string | null;
  /** 旧订单签约号（upgradeRefundAmount 实际返回字段） */
  upOldSignNo?: string | null;
  /** 建议的改订类型（如 UPGRADE） */
  upModifyType?: 'UPGRADE' | 'DOWNGRADE' | 'CANCEL' | 'REVERT_CANCEL' | string;
  /** 退款明细列表（新→新升级时可能为空） */
  upRefundItems?: IUpRefundItem[];
}

/**
 * 获取升级会员退款金额
 */
export function getUpgradeRefundAmount(
  data: IUpgradeRefundAmountParams,
  config?: { signal?: AbortSignal }
) {
  return request<IUpgradeRefundAmountResponse>({
    api: "/api/commonOrder/upgradeRefundAmount",
    method: "post",
    data,
    notToast: false,
    ...(config?.signal && { signal: config.signal })
  });
}

/**
 * 根据订单ID查询合同
 * @param transactionId 订单ID
 */
export function queryContractByTransactionId(transactionId: string) {
  return request<ICreateContractResponse>({
    api: "/api/contract/queryContractByTransactionId",
    method: "post",
    data: {
      transactionId
    },
    notToast: false
  });
}
/**
 * 获取商品算力数据响应类型
 */
export interface ICommodityDataResp {
  /** 购买商品后下发的算力值 */
  points: number;
}

/**
 * 通过商品id获取购买后下发的算力值
 */
export function getCommodityData(commodityId: string) {
  return request<ICommodityDataResp>({
    api: '/api/commonOrder/getCommodityData',
    method: 'post',
    data: { commodityId },
    notToast: false
  });
}

/**
 * 添加企微
 */
export function addEnterpriseWeChatContact() {
  return request<any>({
    api: "api/qiwei/addContact",
    method: "post",
    data: {},
    notToast: false
  });
}

/**
 * 调用解约API
 */
export const unSignedApi = async (): Promise<UnSignedResponse> => {
  const response = await request<UnSignedResponse>({
    api: "/api/commonOrder/unSigned",
    method: "POST",
    notToast: false
  });

  return response.data;
};
